import { SidebarContent, SidebarFooter, SidebarHeader } from "@/components/ui/sidebar.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { api } from "@/lib/http.ts";
import { apiErrorMessage } from "@/shared/apiErrorMessage.ts";
import { cn } from "@/lib/utils.ts";
import type { ReminderChannel } from "@/types/calendarEvent.ts";
import type { CalendarEvent, RecurringEditScope } from "@/types/events.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowUpRight,
  Bell,
  CalendarDays,
  Clock,
  Loader2,
  Repeat,
  StickyNote,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { deleteEvent, updateEvent } from "./api.ts";
import {
  DatePickerCalendar,
  EventColorPicker,
  SectionIcon,
  autoGrowTextareaClass,
} from "./eventPanelFields.tsx";
import { rruleToText } from "@/shared/rrule.ts";
import { RecurringEditScopeDialog } from "./RecurringEditScopeDialog.tsx";
import {
  REMINDER_BASELINE_CHANNELS,
  REMINDER_CHANNEL_LABELS,
  reminderPanelChannels,
} from "@/shared/reminderChannels.ts";
import { reminderQueries } from "@/shared/reminderQueries.ts";
import { formatTimeLabelCompact, timeOptionsWithValue } from "./timeOptions.ts";

/**
 * The reminder counterpart to `EventDetailPanel`.
 *
 * A reminder is a moment that pings someone — it books no time, holds no
 * guests, and nobody replies to it. So this panel deliberately shows a much
 * smaller set of fields than the event panel: title, date and time, repeat,
 * recipients, channel, notes, colour, delete. Everything an event panel offers
 * that presumes a meeting (end time, all-day, free/busy, location, video, RSVP,
 * a guest list, the lead-time alert editor, a calendar picker) is absent by
 * design rather than by omission.
 *
 * A separate component rather than a branch inside the 1900-line event panel:
 * almost nothing is shared beyond the field primitives, which are imported.
 */
export const ReminderDetailPanel = ({
  event,
  onClose,
  onExpand,
}: {
  event: CalendarEvent;
  onClose: () => void;
  /**
   * Promote this panel to the fuller surface for the same reminder, from its own
   * title bar. Omitted where the panel already *is* that surface.
   *
   * Mirrors `EventDetailPanel`'s prop of the same name. A host that offers the
   * affordance passes it to this panel *and* to that one: the calendar popover
   * picks between them per chip, so wiring only one leaves half the chips on the
   * grid with no way through.
   */
  onExpand?: () => void;
}) => {
  const queryClient = useQueryClient();

  const start = new Date(event.start.dateTime);
  const [summary, setSummary] = useState(event.summary);
  const [description, setDescription] = useState(event.description ?? "");
  const [startDate, setStartDate] = useState<Date>(start);
  const [startTime, setStartTime] = useState(format(start, "HH:mm"));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [scopeRequest, setScopeRequest] = useState<null | "edit" | "delete">(null);

  const sourceEntId = event.gcalId ?? "";
  const isRecurring = !!(event.isRecurring || event.recurringEventId);
  const recipients = (event.participants ?? []).filter((p) => !p.self);
  const remindBy: ReminderChannel =
    event.okthx?.kind === "reminder" ? event.okthx.remindBy : "text";
  const channelLabel = REMINDER_CHANNEL_LABELS[remindBy];
  const isSelfReminder = recipients.length === 0;
  // What reaches the user bounds their own reminder, and whether their own
  // WhatsApp is open bounds a WhatsApp reminder for anyone else.
  const { data: availableChannels = REMINDER_BASELINE_CHANNELS } = useQuery(
    reminderQueries.channels(),
  );
  const channelOptions = reminderPanelChannels({
    current: remindBy,
    isSelfReminder,
    availableChannels,
  });

  const summaryDirty = summary !== event.summary;
  const descriptionDirty = description !== (event.description ?? "");
  // Tracked separately from the text fields because a series-scoped save sends
  // the time through a re-anchor — see `save`.
  const timeDirty =
    format(startDate, "yyyy-MM-dd") !== format(start, "yyyy-MM-dd") ||
    startTime !== format(start, "HH:mm");
  const dirty = summaryDirty || descriptionDirty || timeDirty;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
    queryClient.invalidateQueries({ queryKey: ["event", event.gcalId] });
  };

  const saveMutation = useMutation({
    mutationFn: updateEvent,
    onSuccess: invalidate,
    // Matches the event panel's convention: refetch rather than restore a
    // captured snapshot, so there is one way rollback works on this surface.
    onError: () => {
      errorToast("Couldn't save the reminder");
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: () => errorToast("Couldn't delete the reminder"),
  });

  const channelMutation = useMutation({
    mutationFn: async (next: ReminderChannel) => {
      await api.patch(`/api/a/reminders/${encodeURIComponent(sourceEntId)}/channel`, {
        remindBy: next,
        // How a repeating reminder reaches you is a property of the reminder,
        // not of one occurrence — nobody means "email me only this Tuesday".
        // Scoped to the series so the master and every future occurrence follow,
        // which is also why this control raises no scope dialog.
        ...(isRecurring ? { editScope: "allEvents" as const } : {}),
      });
    },
    onSuccess: invalidate,
    onError: (error) => {
      // The server refuses a change it can't honour, and says why: a channel
      // needing a different kind of address than the recipients have, or one
      // that doesn't reach the user yet (WhatsApp not connected). The options
      // come from cached data that can be stale, so a generic toast would leave
      // the refusal unexplained.
      errorToast(apiErrorMessage(error) ?? "Couldn't change how this reminder is sent");
    },
  });

  /**
   * Send only the fields the user actually changed.
   *
   * Sending the time unconditionally is not harmless here: a series-scoped save
   * re-anchors every future occurrence to the time it carries. So renaming a
   * daily reminder while looking at an occurrence that had been snoozed would
   * move the whole series to the snoozed time — a field the user never touched
   * rewriting one they did.
   */
  const save = (editScope: RecurringEditScope) => {
    const times: { startDateTime?: string; endDateTime?: string } = {};
    if (timeDirty) {
      const [hours, minutes] = startTime.split(":").map(Number);
      const when = new Date(startDate);
      when.setHours(hours ?? 0, minutes ?? 0, 0, 0);
      const iso = when.toISOString();
      // A reminder is a moment: start and end are the same instant.
      times.startDateTime = iso;
      times.endDateTime = iso;
    }
    saveMutation.mutate({
      sourceEntId,
      calendarId: event.calendarId,
      ...(summaryDirty ? { summary } : {}),
      ...(descriptionDirty ? { description } : {}),
      ...times,
      editScope,
    });
  };

  const handleSave = () => {
    if (isRecurring) {
      setScopeRequest("edit");
      return;
    }
    save("thisEvent");
  };

  const handleDelete = (editScope: RecurringEditScope) => {
    deleteMutation.mutate({
      sourceEntId,
      calendarId: event.calendarId,
      editScope,
    });
  };

  const isPending = saveMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <SidebarHeader className="shrink-0 flex flex-row items-start gap-2 px-4 py-5">
        <Textarea
          rows={1}
          aria-label="Reminder title"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className={cn(
            "min-h-0 resize-none border-transparent px-1 text-lg font-semibold shadow-none focus-visible:border-input",
            autoGrowTextareaClass,
          )}
        />
        <Button
          variant="ghost"
          size="icon"
          type="button"
          aria-label="Delete reminder"
          data-statsig-name="reminder-panel-delete"
          onClick={() => (isRecurring ? setScopeRequest("delete") : setDeleteOpen(true))}
          className="size-10 shrink-0"
        >
          <Trash2 className="size-4" />
        </Button>
        {/* Before the dismiss, matching the event panel beside it: the two
            controls that are about where you are reading, rather than about the
            reminder, sit together. */}
        {onExpand && (
          <Button
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Expand"
            data-statsig-name="reminder-panel-expand"
            onClick={onExpand}
            className="size-10 shrink-0"
          >
            <ArrowUpRight className="size-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          type="button"
          aria-label="Close"
          data-statsig-name="reminder-panel-close"
          onClick={onClose}
          className="size-10 shrink-0"
        >
          <X className="size-4" />
        </Button>
      </SidebarHeader>

      <SidebarContent className="flex flex-col gap-4 px-4 pb-4">
        {/* Date */}
        <div className="flex items-center gap-3">
          <SectionIcon icon={CalendarDays} label="Date" />
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="xs"
                data-statsig-name="reminder-panel-date"
              >
                {format(startDate, "MMMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <DatePickerCalendar selected={startDate} onSelect={setStartDate} />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time — one instant, never a range. */}
        <div className="flex items-center gap-3">
          <SectionIcon icon={Clock} label="Time" />
          <Select value={startTime} onValueChange={setStartTime}>
            <SelectTrigger variant="editable" className="h-7 w-auto px-2 text-sm" aria-label="Time">
              {formatTimeLabelCompact(startTime)}
            </SelectTrigger>
            <SelectContent>
              {timeOptionsWithValue(startTime).map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Repeat — text, not a control. Nothing in the product changes a
            reminder's pattern: the rrule is set when the series is created, and
            moving a reminder re-anchors its time of day while leaving the rule
            alone. A picker would offer an edit that has nowhere to go, and a
            disabled one reads as broken rather than as fixed by design.

            Plain foreground rather than the event panel's muted read-only
            recurrence line — muted is what made it look disabled. Absent
            entirely when the reminder doesn't repeat, the way the recipients
            list is absent when it's just for the user. */}
        {event.recurrence?.[0] && (
          <div className="flex items-center gap-3">
            <SectionIcon icon={Repeat} label="Repeat" />
            <span className="text-sm">{rruleToText(event.recurrence[0], startDate)}</span>
          </div>
        )}

        {/* Recipients — hidden when the reminder is just for the user. */}
        {recipients.length > 0 && (
          <div className="flex items-start gap-3">
            <SectionIcon icon={Users} label="Who it goes to" />
            {/* A real list, labelled rather than relying on the icon's tooltip:
                that only exists on hover, and a screen reader reaches this cold. */}
            <ul aria-label="Who it goes to" className="flex min-w-0 flex-col gap-0.5">
              {recipients.map((person) => {
                const label = person.name ?? person.email;
                return (
                  <li key={person.urn} className="truncate text-sm">
                    {person.contactId ? (
                      // A real link: the nav boundary decides where a contact
                      // opens, and cmd/middle-click keep working.
                      <Link
                        href={`/contacts/${person.contactId}`}
                        aria-label={`Open ${label}`}
                        className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {label}
                      </Link>
                    ) : (
                      label
                    )}
                    {person.name && person.email && (
                      <span className="ml-1.5 text-muted-foreground">{person.email}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Channel. A reminder for other people is stored as the exact address it
            goes to, so it can only move to a channel that uses the same kind of
            address: text and WhatsApp share a phone number, email stands alone.
            With nothing else to pick it reads as text, since a control there
            could only produce an error. */}
        <div className="flex items-center gap-3">
          <SectionIcon icon={Bell} label="How it's sent" />
          {channelOptions.length < 2 ? (
            <span className="text-sm text-muted-foreground">{channelLabel}</span>
          ) : (
            <Select
              value={remindBy}
              onValueChange={(next) => {
                const channel = channelOptions.find((option) => option === next);
                if (channel && channel !== remindBy) channelMutation.mutate(channel);
              }}
            >
              <SelectTrigger
                variant="editable"
                className="h-7 w-auto px-2 text-sm"
                aria-label="How it's sent"
              >
                {channelLabel}
              </SelectTrigger>
              <SelectContent>
                {channelOptions.map((channel) => (
                  <SelectItem key={channel} value={channel}>
                    {REMINDER_CHANNEL_LABELS[channel]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Notes */}
        <div className="flex items-start gap-3">
          <SectionIcon icon={StickyNote} label="Notes" />
          <Textarea
            rows={1}
            aria-label="Notes"
            placeholder="Add a note"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={cn("min-h-8 resize-none text-sm", autoGrowTextareaClass)}
          />
        </div>

        {/* Colour */}
        <EventColorPicker
          calendarId={event.calendarId}
          colorId={event.colorId}
          calendarName={event.calendarName}
          onChange={(colorId) =>
            saveMutation.mutate({
              sourceEntId,
              calendarId: event.calendarId,
              colorId: colorId ?? "",
              // Like the channel, a colour describes the reminder rather than
              // one of its occurrences — recolouring a single chip of a daily
              // reminder is not what anyone means by picking a colour.
              ...(isRecurring ? { editScope: "allEvents" as const } : {}),
            })
          }
        />
      </SidebarContent>

      {dirty && (
        <SidebarFooter className="flex-row gap-2 px-4 py-3">
          <Button
            type="button"
            variant="outline"
            data-statsig-name="reminder-panel-cancel"
            className="flex-1"
            disabled={isPending}
            onClick={() => {
              setSummary(event.summary);
              setDescription(event.description ?? "");
              setStartDate(start);
              setStartTime(format(start, "HH:mm"));
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-statsig-name="reminder-panel-save"
            className="flex-1"
            disabled={isPending}
            onClick={handleSave}
          >
            {saveMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Save
          </Button>
        </SidebarFooter>
      )}

      <RecurringEditScopeDialog
        open={scopeRequest !== null}
        mode={scopeRequest === "delete" ? "delete" : "edit"}
        isPending={isPending}
        onClose={() => setScopeRequest(null)}
        onConfirm={(scope) => {
          const request = scopeRequest;
          setScopeRequest(null);
          if (request === "delete") handleDelete(scope);
          else if (request === "edit") save(scope);
        }}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete this reminder?</DialogTitle>
            <DialogDescription>It won't be sent.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-statsig-name="reminder-delete-cancel"
              onClick={() => setDeleteOpen(false)}
            >
              Keep it
            </Button>
            <Button
              type="button"
              variant="destructive"
              data-statsig-name="reminder-delete-confirm"
              disabled={isPending}
              onClick={() => {
                setDeleteOpen(false);
                handleDelete("thisEvent");
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
